import type { ScreenerRespEntity } from '@revelio/data-access';

import type { ScreenerMetricValues } from '../../PageConfigProvider/types/metric';

export type ScreenerMetricField =
  | 'headcount'
  | 'growth'
  | 'growth_yoy'
  | 'hiring'
  | 'attrition'
  | 'salary'
  | 'tenure'
  | 'inflow'
  | 'outflow';

export const mapMetricToField = (
  m: ScreenerMetricValues
): ScreenerMetricField => {
  switch (m) {
    case 'screener_headcount':
      return 'headcount';
    case 'screener_growth':
      return 'growth';
    case 'screener_growth_yoy':
      return 'growth_yoy';
    case 'screener_hiring':
      return 'hiring';
    case 'screener_attrition':
      return 'attrition';
    case 'screener_salary':
      return 'salary';
    case 'screener_tenure':
      return 'tenure';
    case 'screener_inflow':
      return 'inflow';
    case 'screener_outflow':
      return 'outflow';
    default:
      return 'headcount';
  }
};

export const processScreenerEntityData = (
  entities: ScreenerRespEntity[],
  metric: ScreenerMetricValues
) => {
  const field = mapMetricToField(metric);
  return entities
    .map((entity) => {
      const label = entity.long_name || entity.short_name || '';
      let series: number[] | undefined;
      switch (field) {
        case 'headcount':
          series = entity.headcount;
          break;
        case 'growth':
          series = entity.growth;
          break;
        case 'growth_yoy':
          series = entity.growth_yoy;
          break;
        case 'hiring':
          series = entity.hiring;
          break;
        case 'attrition':
          series = entity.attrition;
          break;
        case 'salary':
          series = entity.salary;
          break;
        case 'tenure':
          series = entity.tenure;
          break;
        case 'inflow':
          series = entity.inflow;
          break;
        case 'outflow':
          series = entity.outflow;
          break;
        default:
          series = undefined;
      }
      const value = Array.isArray(series)
        ? series[series.length - 1]
        : undefined;
      if (value == null) return null;
      return { dim: label, value };
    })
    .filter((x): x is { dim: string; value: number } => x !== null);
};

export const processMultipleScreenerMetrics = (
  entities: ScreenerRespEntity[],
  metrics: ScreenerMetricValues[]
) => {
  return metrics.reduce<
    Record<ScreenerMetricValues, Array<{ dim: string; value: number }>>
  >(
    (acc, metric) => {
      acc[metric] = processScreenerEntityData(entities, metric);
      return acc;
    },
    {} as Record<ScreenerMetricValues, Array<{ dim: string; value: number }>>
  );
};
